import React, { useState, useMemo, useEffect } from 'react';
import { Box, Typography, Paper, Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Menu, Divider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { motion, AnimatePresence } from 'framer-motion';
import { MdChevronLeft, MdChevronRight, MdEvent, MdAccessTime, MdPerson, MdEdit, MdDelete, MdCheckCircle, MdCancel, MdMoreVert } from 'react-icons/md';
import moment from 'moment';
import { useTenant } from '../context/TenantContext';

const TIME_SLOTS = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0');
  return `${hour}:00`;
});

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return '#4caf50';
    case 'cancelled':
      return '#f44336';
    case 'pending':
      return '#ff9800';
    case 'rescheduled':
      return '#2196f3';
    default:
      return '#9e9e9e';
  }
};

const AppointmentSlot = ({ appointment, onAppointmentClick, onEdit, onDelete, onComplete, onCancel, onStatusChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [statusMenuEl, setStatusMenuEl] = useState(null);
  const open = Boolean(anchorEl);
  const statusMenuOpen = Boolean(statusMenuEl);
  const { tenant } = useTenant();

  // Get available statuses from tenant config or use defaults
  const availableStatuses = tenant?.config?.appointment_rules?.statuses || 
    ['pending', 'confirmed', 'in_progress', 'completed', 'cancelled', 'no_show'];

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusClick = (event) => {
    event.stopPropagation();
    setStatusMenuEl(event.currentTarget);
  };

  const handleStatusClose = () => {
    setStatusMenuEl(null);
  };

  const handleStatusSelect = (newStatus) => {
    if (onStatusChange && newStatus !== appointment.status) {
      onStatusChange(appointment.appointment_id, newStatus);
    }
    handleStatusClose();
  };

  const startTime = moment(appointment.start_time, 'HH:mm:ss').format('hh:mm A');
  const endTime = moment(appointment.end_time, 'HH:mm:ss').format('hh:mm A');
  const statusColor = getStatusColor(appointment.status);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      onClick={(e) => {
        e.stopPropagation();
        onAppointmentClick?.(appointment);
      }}
      className="cursor-pointer"
    >
      <Paper
        elevation={2}
        sx={{
          p: 1.5,
          mb: 1,
          borderRadius: '8px',
          borderLeft: `4px solid ${statusColor}`,
          backgroundColor: 'white',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 4,
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {appointment.patient_name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <MdPerson size={14} color="#666" />
              <Typography variant="caption" color="text.secondary">
                {appointment.staff_name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MdAccessTime size={14} color="#666" />
              <Typography variant="caption" color="text.secondary">
                {startTime} - {endTime}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              label={appointment.status}
              size="small"
              onClick={handleStatusClick}
              sx={{
                backgroundColor: `${statusColor}20`,
                color: statusColor,
                fontWeight: 600,
                fontSize: '0.7rem',
                height: '20px',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: `${statusColor}30`,
                },
              }}
            />
            <IconButton size="small" onClick={handleClick}>
              <MdMoreVert size={16} />
            </IconButton>
          </Box>
        </Box>
      </Paper>
      
      {/* Status Change Menu */}
      <Menu
        anchorEl={statusMenuEl}
        open={statusMenuOpen}
        onClose={handleStatusClose}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            minWidth: 180,
            borderRadius: '8px',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 1, py: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Change Status
          </Typography>
        </Box>
        <Divider />
        {availableStatuses.map((status) => {
          const isCurrentStatus = status === appointment.status;
          const statusColorForMenu = getStatusColor(status);
          return (
            <MenuItem
              key={status}
              onClick={() => handleStatusSelect(status)}
              disabled={isCurrentStatus}
              sx={{
                textTransform: 'capitalize',
                color: isCurrentStatus ? 'text.disabled' : 'text.primary',
                '&:hover': {
                  backgroundColor: `${statusColorForMenu}10`,
                },
                '&.Mui-disabled': {
                  opacity: 0.6,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: statusColorForMenu,
                  }}
                />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {status.replace('_', ' ')}
                </Typography>
                {isCurrentStatus && (
                  <Typography variant="caption" color="text.secondary">
                    (Current)
                  </Typography>
                )}
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    </motion.div>
  );
};

const AppointmentCalendar = ({ 
  appointments = [], 
  selectedDate, 
  onDateChange,
  onAppointmentClick,
  onEdit,
  onDelete,
  onComplete,
  onCancel,
  onCreateAppointment,
  onStatusChange
}) => {
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'timeline'
  const [currentMonth, setCurrentMonth] = useState(() => {
    return selectedDate ? dayjs(selectedDate) : dayjs();
  });

  // Sync currentMonth with selectedDate when it changes
  useEffect(() => {
    if (selectedDate) {
      const selected = dayjs(selectedDate);
      if (!selected.isSame(currentMonth, 'month')) {
        setCurrentMonth(selected);
      }
    }
  }, [selectedDate, currentMonth]);

  // Group appointments by date
  const appointmentsByDate = useMemo(() => {
    const grouped = {};
    appointments.forEach(apt => {
      const date = moment(apt.start_date).format('YYYY-MM-DD');
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(apt);
    });
    return grouped;
  }, [appointments]);

  // Get appointments for selected date
  const selectedDateAppointments = useMemo(() => {
    if (!selectedDate) return [];
    const dateKey = moment(selectedDate).format('YYYY-MM-DD');
    return appointmentsByDate[dateKey] || [];
  }, [selectedDate, appointmentsByDate]);

  // Group appointments by time slot for timeline view
  const appointmentsByTimeSlot = useMemo(() => {
    const slots = {};
    selectedDateAppointments.forEach(apt => {
      const startHour = moment(apt.start_time, 'HH:mm:ss').hour();
      const slotKey = `${startHour.toString().padStart(2, '0')}:00`;
      if (!slots[slotKey]) {
        slots[slotKey] = [];
      }
      slots[slotKey].push(apt);
    });
    return slots;
  }, [selectedDateAppointments]);

  const handlePreviousMonth = () => {
    setCurrentMonth(prev => prev.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => prev.add(1, 'month'));
  };

  const handleDateSelect = (date) => {
    if (date) {
      onDateChange?.(date.toDate());
      setCurrentMonth(date);
    }
  };

  const handleMonthChange = (date) => {
    if (date) {
      setCurrentMonth(date);
    }
  };

  const isDateHasAppointments = (date) => {
    const dateKey = moment(date).format('YYYY-MM-DD');
    return appointmentsByDate[dateKey]?.length > 0;
  };

  const getAppointmentCount = (date) => {
    const dateKey = moment(date).format('YYYY-MM-DD');
    return appointmentsByDate[dateKey]?.length || 0;
  };

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {/* View Mode Toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant={viewMode === 'calendar' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('calendar')}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              background: viewMode === 'calendar' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
            }}
          >
            Calendar View
          </Button>
          <Button
            variant={viewMode === 'timeline' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('timeline')}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              background: viewMode === 'timeline' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
            }}
          >
            Timeline View
          </Button>
        </Box>
      </Box>

      {viewMode === 'calendar' ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', lg: 'row' },
          gap: 3, 
          height: { xs: 'auto', lg: 'calc(100vh - 240px)' },
          minHeight: { xs: '600px', lg: 'calc(100vh - 240px)' }
        }}>
          {/* Calendar Picker */}
          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: '16px',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              width: { xs: '100%', lg: '350px' },
              minWidth: { xs: '100%', lg: '350px' },
              maxWidth: { xs: '100%', lg: '350px' },
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            {selectedDate && (
              <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: '8px', flexShrink: 0 }}>
                <Typography variant="caption" color="text.secondary">
                  {moment(selectedDate).format('dddd, MMMM DD, YYYY')}
                </Typography>
                <Typography variant="h6" sx={{ mt: 1, fontWeight: 600 }}>
                  {selectedDateAppointments.length} Appointment{selectedDateAppointments.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            )}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar
                value={selectedDate ? dayjs(selectedDate) : dayjs()}
                onChange={handleDateSelect}
                onMonthChange={handleMonthChange}
                defaultCalendarMonth={currentMonth}
                sx={{
                  width: '100%',
                  flex: 1,
                  '& .MuiPickersDay-root': {
                    borderRadius: '8px',
                    position: 'relative',
                    '&.Mui-selected': {
                      backgroundColor: '#667eea',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#764ba2',
                      },
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(102, 126, 234, 0.1)',
                    },
                  },
                  '& .MuiPickersCalendarHeader-root': {
                    paddingLeft: 2,
                    paddingRight: 2,
                  },
                  '& .MuiPickersCalendarHeader-labelContainer': {
                    '& .MuiPickersCalendarHeader-label': {
                      fontWeight: 700,
                      color: '#667eea',
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Paper>

          {/* Appointments List */}
          <Paper
            elevation={3}
            sx={{
              flex: 1,
              p: 3,
              borderRadius: '16px',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              overflow: 'auto',
              minHeight: { xs: '500px', lg: '100%' },
              height: '100%',
            }}
          >
            {selectedDate ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea' }}>
                    Appointments for {moment(selectedDate).format('MMMM DD, YYYY')}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => onCreateAppointment?.(selectedDate)}
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '12px',
                      textTransform: 'none',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                      },
                    }}
                  >
                    + New Appointment
                  </Button>
                </Box>
                {selectedDateAppointments.length > 0 ? (
                  <AnimatePresence>
                    {selectedDateAppointments
                      .sort((a, b) => moment(a.start_time, 'HH:mm:ss').diff(moment(b.start_time, 'HH:mm:ss')))
                      .map((appointment) => (
                        <AppointmentSlot
                          key={appointment.appointment_id}
                          appointment={appointment}
                          onAppointmentClick={onAppointmentClick}
                          onEdit={onEdit}
                          onDelete={onDelete}
                          onComplete={onComplete}
                          onCancel={onCancel}
                          onStatusChange={onStatusChange}
                        />
                      ))}
                  </AnimatePresence>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '300px',
                      color: 'text.secondary',
                    }}
                  >
                    <MdEvent size={64} color="#ccc" />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                      No appointments scheduled
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Click "New Appointment" to schedule one
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: 'text.secondary',
                }}
              >
                <MdEvent size={64} color="#ccc" />
                <Typography variant="h6" sx={{ mt: 2 }}>
                  Select a date to view appointments
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      ) : (
        /* Timeline View */
        <Paper
          elevation={3}
          sx={{
            p: 3,
            borderRadius: '16px',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            height: 'calc(100vh - 240px)',
            overflow: 'auto',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea' }}>
              Timeline View - {selectedDate ? moment(selectedDate).format('MMMM DD, YYYY') : 'Select a date'}
            </Typography>
            {selectedDate && (
              <Button
                variant="contained"
                onClick={() => onCreateAppointment?.(selectedDate)}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '12px',
                  textTransform: 'none',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  },
                }}
              >
                + New Appointment
              </Button>
            )}
          </Box>
          {selectedDate ? (
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Time Slots */}
              <Box sx={{ minWidth: '100px' }}>
                {TIME_SLOTS.map((slot) => (
                  <Box
                    key={slot}
                    sx={{
                      height: '80px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      pt: 1,
                      borderBottom: '1px solid #e0e0e0',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {moment(slot, 'HH:mm').format('hh:mm A')}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {/* Appointments Timeline */}
              <Box sx={{ flex: 1, position: 'relative' }}>
                {TIME_SLOTS.map((slot) => {
                  const slotAppointments = appointmentsByTimeSlot[slot] || [];
                  return (
                    <Box
                      key={slot}
                      sx={{
                        height: '80px',
                        borderBottom: '1px solid #e0e0e0',
                        position: 'relative',
                      }}
                    >
                      {slotAppointments.map((appointment, idx) => {
                        const startTime = moment(appointment.start_time, 'HH:mm:ss');
                        const endTime = moment(appointment.end_time, 'HH:mm:ss');
                        const duration = endTime.diff(startTime, 'minutes');
                        const topOffset = startTime.minute() * (80 / 60);
                        const height = (duration / 60) * 80;

                        return (
                          <motion.div
                            key={appointment.appointment_id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            onClick={() => onAppointmentClick?.(appointment)}
                            style={{
                              position: 'absolute',
                              top: `${topOffset}px`,
                              left: `${idx * 10}px`,
                              width: `calc(100% - ${idx * 10}px)`,
                              height: `${Math.max(height, 40)}px`,
                              zIndex: 10 - idx,
                            }}
                          >
                            <Paper
                              elevation={2}
                              sx={{
                                p: 1,
                                height: '100%',
                                borderRadius: '6px',
                                borderLeft: `4px solid ${getStatusColor(appointment.status)}`,
                                backgroundColor: 'white',
                                cursor: 'pointer',
                                overflow: 'hidden',
                                '&:hover': {
                                  boxShadow: 4,
                                },
                              }}
                            >
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                {appointment.patient_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                {moment(appointment.start_time, 'HH:mm:ss').format('hh:mm A')} - {moment(appointment.end_time, 'HH:mm:ss').format('hh:mm A')}
                              </Typography>
                            </Paper>
                          </motion.div>
                        );
                      })}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'text.secondary',
              }}
            >
              <MdEvent size={64} color="#ccc" />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Select a date to view timeline
              </Typography>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default AppointmentCalendar;

